// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Modal } from "react-bootstrap";

import { ActionType, IAction, IActionChangeTariff, raiseAction } from "actions";
import { DataState } from "types";
import { isSet } from "shared/utils/utils";
import { tabClickHandler } from "guiutils";
import { IState } from "states/states";
import { ModalPanel } from "states/guistates";
import { ITariff } from "states/ocppstates";

import IconAdd from "icons/iconAdd";
import SaveFeedback from "view/comp/SaveFeedback";

type IAddGlobalTariffProps = {
  isOpen: boolean;
  dataState: DataState;
  tariffs: ITariff[];
};

const mapStateToProps = (state: IState) => {
  const props: IAddGlobalTariffProps = {
    isOpen: state.gui.modal.panel === ModalPanel.GLOBAL_TARIFF,
    dataState: state.gui.modal.dataState,
    tariffs: state.ocpp.siteTariff,
  };
  return props;
};

interface IAddGlobalTariffState {}

interface IAddGlobalTariffDispatch {
  onAddClick: (id: number) => void;
  onCancelClick: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IAddGlobalTariffDispatch = {
    onAddClick: (id: number) => {
      const payload: IActionChangeTariff = {
        id,
        name: undefined,
        isActive: true,
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_OCPP_TARIFF_REQ, payload));
    },
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msgAdd: {
    id: "add this global tariff to site",
  },
});

class AddGlobalTariffItemComponent extends React.Component<
  IAddGlobalTariffProps & IAddGlobalTariffDispatch,
  IAddGlobalTariffState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public state: IAddGlobalTariffState = {};

  public render() {
    if (!this.props.isOpen) return <></>; // avoid rendering of inexisting data

    return (
      <Modal show={this.props.isOpen} onHide={this.props.onCancelClick} id="modal-addglobaltariff">
        <Modal.Header closeButton>
          <FormattedMessage id="Add Global or Removed Tariff" />
        </Modal.Header>
        <Modal.Body>{this.renderList()}</Modal.Body>
        <Modal.Footer>
          <div className="button-bar-right">
            <SaveFeedback dataState={this.props.dataState} className="me-2" />
            <Button
              id="cancelAddGlobalTariff"
              variant="secondary"
              className="me-1"
              onClick={this.props.onCancelClick}
            >
              <FormattedMessage id="Cancel" />
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }

  private renderList() {
    // non-associated global tariffs have isActive === undefined
    const listItems = this.props.tariffs
      .filter((tariff) => !isSet(tariff.isActive))
      .map((tariff) => {
        return (
          <tr key={tariff.id}>
            <td className="img-link">
              <span
                tabIndex={0}
                onKeyDown={tabClickHandler}
                onClick={(_evt) => this.props.onAddClick(tariff.id)}
                title={this.context.formatMessage(messages.msgAdd)}
              >
                <IconAdd />
              </span>
            </td>
            <td>{tariff.name}</td>
          </tr>
        );
      });
    if (listItems.length === 0) {
      return (
        <div>
          <FormattedMessage id="No global tariffs available" />
        </div>
      );
    }
    return (
      <table>
        <tbody>{listItems}</tbody>
      </table>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AddGlobalTariffItemComponent);
